/** @jsxImportSource ../i18n */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FileText, Maximize2, Minimize2, RefreshCw, Sparkles } from 'lucide-react';
import api from '../services/api';
import { reportDate, reportTime } from '../components/reportShared';
import { MarkdownProse } from '../components/markdown/MarkdownProse';
import { DigestLines, digestSignature } from '../components/reports/DigestLines';
import { cx } from '../theme/cx';
import { Badge, Banner, Button, Card, EmptyState, PageHeader, Skeleton, SkeletonText } from '../components/ui';

const PAGE_SIZE = 30;
const GENERATE_POLL_MS = 20000;
const GENERATE_TIMEOUT_MS = 15 * 60 * 1000;
const validReportId = value => /^[1-9][0-9]*$/.test(String(value)) && Number.isSafeInteger(Number(value));

const ARTICLE = 'min-w-0 rounded-card border border-line bg-surface-1 p-5 shadow-card sm:p-8';
const ARTICLE_FULLSCREEN = 'fixed inset-0 z-50 overflow-y-auto scroll-pt-16 bg-canvas';
const WIDE = 'mx-auto max-w-screen-xl px-4 sm:px-10';
// Text keeps a reading measure; tables (the focusable regions) break out to the full width.
const TEXT = 'mx-auto max-w-[46rem]';
const PROSE_COLUMNS = '[&>*]:mx-auto [&>*]:max-w-[46rem] [&>[role=region]]:max-w-none';

const reportTitle = report => report.subject || `Rapport du ${reportDate(report.report_date)}`;

const ReportStatus = ({ report }) => {
  if (report.error) return <Badge tone="warn" dot>Incident signalé</Badge>;
  return report.email_sent_at ? <Badge tone="gain" dot>Envoyé par email</Badge> : <Badge>Email non envoyé</Badge>;
};

const Meta = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="eyebrow">{label}</dt>
    <dd className="mt-1.5 break-words text-sm text-fg-body">{children}</dd>
  </div>
);

const ListSkeleton = () => (
  <div role="status" className="space-y-2">
    <span className="sr-only">Chargement de l'historique…</span>
    {Array.from({ length: 4 }, (_, i) => (
      <div key={i} aria-hidden="true" className="rounded-control border border-line p-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-3 h-3 w-full" />
        <Skeleton className="mt-3 h-5 w-24 rounded-full" />
      </div>
    ))}
  </div>
);

const DailyReports = () => {
  const { reportId } = useParams();
  const [reports, setReports] = useState([]);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [loadedLimit, setLoadedLimit] = useState(PAGE_SIZE);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [listVersion, setListVersion] = useState(0);
  const [report, setReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailVersion, setDetailVersion] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generationSince, setGenerationSince] = useState(0);
  const [generateNotice, setGenerateNotice] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [compact, setCompact] = useState(false);
  const baselineCreatedAt = useRef('');
  const invalidId = Boolean(reportId && !validReportId(reportId));
  const selectedId = reportId ? (invalidId ? null : reportId) : reports[0] && validReportId(reports[0].id) ? String(reports[0].id) : null;
  const currentReport = report && String(report.id) === selectedId ? report : null;

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setListLoading(true);
    setListError('');
    api.get('/analysis/morning-reports', { params: { limit }, signal: controller.signal })
      .then(response => {
        if (!Array.isArray(response.data)) throw new Error('Invalid report list');
        if (active) {
          setReports(response.data);
          setLoadedLimit(limit);
        }
      })
      .catch(() => {
        if (active) setListError("Impossible de charger l'historique des rapports.");
      })
      .finally(() => {
        if (active) setListLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [limit, listVersion]);

  useEffect(() => {
    setReport(null);
    setDetailError('');
    if (!selectedId) {
      setDetailLoading(false);
      return undefined;
    }
    let active = true;
    const controller = new AbortController();
    setDetailLoading(true);
    api.get(`/analysis/morning-reports/${encodeURIComponent(selectedId)}`, { signal: controller.signal })
      .then(response => {
        if (String(response.data?.id) !== selectedId || typeof response.data?.content !== 'string') throw new Error('Invalid report detail');
        if (active) setReport(response.data);
      })
      .catch(error => {
        if (active) setDetailError([403, 404].includes(error.response?.status) ? 'Rapport introuvable ou non accessible.' : 'Impossible de charger ce rapport.');
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [selectedId, detailVersion]);

  // Escape leaves the fullscreen reading mode.
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      setCompact(false);
    };
  }, [fullscreen]);

  // While a generation triggered here runs server-side, poll the archive until
  // today's upserted report appears (created_at differs from the baseline).
  useEffect(() => {
    if (!generationSince) return undefined;
    const since = generationSince;
    const timer = setInterval(() => {
      api.get('/analysis/morning-reports', { params: { limit: 1 } })
        .then(response => {
          const newest = Array.isArray(response.data) ? response.data[0] : null;
          if (newest && String(newest.created_at) !== baselineCreatedAt.current) {
            setGenerationSince(0);
            setGenerating(false);
            setGenerateNotice({ kind: 'info', text: 'Nouveau rapport généré.' });
            setListVersion(version => version + 1);
            setDetailVersion(version => version + 1);
          } else if (Date.now() - since > GENERATE_TIMEOUT_MS) {
            setGenerationSince(0);
            setGenerating(false);
            setGenerateNotice({ kind: 'error', text: "La génération prend plus de temps que prévu : elle se poursuit peut-être en arrière-plan. Actualisez dans quelques minutes." });
          }
        })
        .catch(() => {});
    }, GENERATE_POLL_MS);
    return () => clearInterval(timer);
  }, [generationSince]);

  const refresh = () => {
    setListVersion(version => version + 1);
    setDetailVersion(version => version + 1);
  };

  const generate = () => {
    if (generating) return;
    setGenerateNotice(null);
    setGenerating(true);
    api.post('/analysis/morning-report')
      .then(() => {
        baselineCreatedAt.current = (reports[0] && reports[0].created_at) || '';
        setGenerationSince(Date.now());
        setGenerateNotice({ kind: 'info', text: "Génération lancée : le rapport du jour sera remplacé (consensus d'experts si au moins deux experts sont activés) puis renvoyé par email. Cela peut prendre plusieurs minutes." });
      })
      .catch((err) => {
        setGenerating(false);
        setGenerateNotice({ kind: 'error', text: err?.response?.data?.error || 'Impossible de lancer la génération du rapport.' });
      });
  };

  const toggle = (
    <Button
      variant="secondary"
      size="sm"
      leadingIcon={fullscreen ? Minimize2 : Maximize2}
      onClick={() => setFullscreen(value => !value)}
      aria-pressed={fullscreen}
      aria-label={fullscreen ? 'Réduire le rapport' : 'Afficher le rapport en plein écran'}
      className="flex-shrink-0"
    >
      <span className="hidden sm:inline">{fullscreen ? 'Réduire' : 'Plein écran'}</span>
    </Button>
  );

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Intelligence"
        title="Rapports quotidiens"
        description={(
          <>
            <p>Retrouvez les analyses enregistrées, même si leur envoi par email a échoué.</p>
            <p className="mt-1 text-sm text-fg-subtle">La consultation et l'actualisation ne déclenchent aucune analyse ni recherche Tavily.</p>
          </>
        )}
        actions={(
          <>
            <Button variant="secondary" leadingIcon={RefreshCw} loading={listLoading || detailLoading} onClick={refresh} aria-label="Actualiser les rapports">
              Actualiser
            </Button>
            <Button leadingIcon={Sparkles} loading={generating} onClick={generate} aria-label="Générer un nouveau rapport">
              {generating ? 'Génération en cours…' : 'Générer un nouveau rapport'}
            </Button>
          </>
        )}
      />
      {generateNotice && (
        <Banner role="status" tone={generateNotice.kind === 'error' ? 'loss' : 'gold'}>{generateNotice.text}</Banner>
      )}
      <div className="grid gap-6 xl:grid-cols-[20rem_minmax(0,1fr)]">
        <Card as="aside" className="self-start" aria-label="Historique des rapports quotidiens">
          <h2 className="mb-4 text-[17px] font-semibold text-fg">Historique</h2>
          {listLoading && reports.length === 0 && <ListSkeleton />}
          {listError && (
            <Banner
              tone="loss"
              role="alert"
              compact
              className="mb-4"
              action={<Button variant="link" onClick={() => setListVersion(version => version + 1)} aria-label="Réessayer le chargement de l'historique">Réessayer</Button>}
            >
              {listError}
            </Banner>
          )}
          {!listLoading && !listError && reports.length === 0 && (
            <p className="text-sm leading-relaxed text-fg-muted">Aucun rapport quotidien enregistré. Les rapports apparaîtront ici après leur génération.</p>
          )}
          <nav className="max-h-80 space-y-2 overflow-y-auto pr-1 xl:max-h-[70vh]" aria-label="Choisir un rapport quotidien">
            {reports.map(item => {
              const active = String(item.id) === selectedId;
              return (
                <Link
                  key={item.id}
                  to={`/reports/${item.id}`}
                  aria-current={active ? 'page' : undefined}
                  className={cx(
                    'relative block rounded-control border p-3 transition-colors duration-150',
                    active ? 'border-gold-500/40 bg-gold-950/40 before:absolute before:inset-y-3 before:left-0 before:w-0.5 before:rounded-full before:bg-gold-500' : 'border-line hover:border-line-strong hover:bg-surface-2/60',
                  )}
                >
                  <p className="font-display text-base text-fg">{reportDate(item.report_date)}</p>
                  <p className="mt-1 line-clamp-2 break-words text-[13px] leading-snug text-fg-muted">{item.subject || 'Analyse matinale PEA'}</p>
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    <ReportStatus report={item} />
                    {item.kind === 'experts' && <Badge tone="gold">Consensus d'experts</Badge>}
                  </div>
                </Link>
              );
            })}
          </nav>
          {reports.length > 0 && reports.length >= loadedLimit && !listError && (
            <Button variant="secondary" size="sm" fullWidth className="mt-4" onClick={() => setLimit(loadedLimit + PAGE_SIZE)} disabled={listLoading}>
              Voir les rapports plus anciens
            </Button>
          )}
        </Card>
        <section className="min-w-0" aria-label="Lecture du rapport quotidien" aria-busy={detailLoading}>
          {invalidId ? (
            <Card>
              <Banner tone="loss" role="alert">Identifiant de rapport invalide.</Banner>
              <Button as={Link} to="/reports" variant="link" className="mt-4">Retour aux rapports</Button>
            </Card>
          ) : detailLoading ? (
            <Card padding="lg" role="status">
              <span className="sr-only">Chargement du rapport…</span>
              <div aria-hidden="true">
                <Skeleton className="h-7 w-2/3" />
                <Skeleton className="mt-5 h-3 w-48" />
                <SkeletonText lines={8} className="mt-8" />
              </div>
            </Card>
          ) : detailError ? (
            <Card>
              <Banner tone="loss" role="alert">{detailError}</Banner>
              <Button variant="secondary" leadingIcon={RefreshCw} className="mt-4" onClick={() => setDetailVersion(version => version + 1)}>
                Réessayer le chargement du rapport
              </Button>
            </Card>
          ) : currentReport ? (
            <article
              className={fullscreen ? ARTICLE_FULLSCREEN : ARTICLE}
              onScroll={fullscreen ? (event) => setCompact(event.currentTarget.scrollTop > 16) : undefined}
            >
              {fullscreen && (
                // Sticky but out of the flow (h-0): the compact bar covers the header as it
                // scrolls away instead of shrinking it, so the text never jumps.
                <div className="sticky top-0 z-10 h-0">
                  <div className={cx('border-b px-4 transition-colors duration-200 sm:px-10', compact ? 'border-line bg-canvas/95 backdrop-blur' : 'border-transparent')}>
                    <div className={cx(TEXT, 'flex h-14 items-center justify-end gap-4')}>
                      <p aria-hidden="true" className={cx('min-w-0 flex-1 truncate font-display text-lg text-fg transition-opacity duration-200', !compact && 'invisible opacity-0')}>
                        {reportTitle(currentReport)}
                      </p>
                      {toggle}
                    </div>
                  </div>
                </div>
              )}
              <div className={fullscreen ? cx(WIDE, 'pb-10 pt-16') : undefined}>
                <header className={cx('mb-8 border-b border-line pb-6', fullscreen && TEXT)}>
                  <div className="flex items-start justify-between gap-4">
                    <h2 className="min-w-0 break-words font-display text-display-md font-normal text-fg">
                      {reportTitle(currentReport)}
                    </h2>
                    {!fullscreen && toggle}
                  </div>
                  <dl className="mt-5 grid gap-x-6 gap-y-4 sm:grid-cols-2">
                    <Meta label="Date du rapport">{reportDate(currentReport.report_date)}</Meta>
                    <Meta label="Généré le (heure de Paris)">{reportTime(currentReport.created_at)}</Meta>
                    <Meta label="Modèle">{currentReport.model || 'Non renseigné'}</Meta>
                    <Meta label="Envoi par email">
                      <ReportStatus report={currentReport} />
                      {currentReport.email_sent_at && <span className="mt-1 block text-xs text-fg-subtle">{reportTime(currentReport.email_sent_at)} (Paris)</span>}
                    </Meta>
                  </dl>
                  {currentReport.error && (
                    <Banner tone="warn" role="alert" className="mt-5">
                      <p className="whitespace-pre-wrap">{currentReport.error}</p>
                    </Banner>
                  )}
                </header>
                {currentReport.digest?.trim() && (
                  <section aria-label="L’essentiel en 5 lignes" className={cx(TEXT, 'mb-8 min-w-0 rounded-r-control border-l-2 border-gold-500 bg-surface-2 px-5 py-4')} data-testid="daily-report-digest">
                    <p className="eyebrow text-gold-400/90">L’essentiel en 5 lignes</p>
                    <DigestLines className="mt-3" lines={currentReport.digest.split('\n').filter((l) => l.trim())} />
                    {currentReport.digest_author && (
                      <p className="mt-3 text-right font-display text-sm italic text-fg-muted">— {digestSignature({ source: 'rapporteur', author: currentReport.digest_author, kind: currentReport.kind })}</p>
                    )}
                  </section>
                )}
                <div className="min-w-0" data-testid="daily-report-content">
                  {currentReport.content.trim()
                    ? <MarkdownProse variant="report" skipHtml className={PROSE_COLUMNS}>{currentReport.content}</MarkdownProse>
                    : <p className={cx(TEXT, 'text-fg-muted')}>Le contenu de ce rapport est vide.</p>}
                </div>
              </div>
            </article>
          ) : (
            <Card>
              <EmptyState
                icon={FileText}
                description={listLoading ? 'Chargement du dernier rapport disponible…' : "Sélectionnez un rapport dans l'historique pour le consulter."}
              />
            </Card>
          )}
        </section>
      </div>
    </div>
  );
};

export default DailyReports;
